import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { X, Instagram, Facebook } from 'lucide-react';
import alertService from '../../utils/alert';
import { fetchConversations } from '../../redux/inboxSlice';
import { linkContactInstagram, linkContactFacebook } from '../../redux/crmSlice';

const CHANNEL_CONFIG = {
  instagram: {
    label: 'Instagram',
    icon: Instagram,
    iconColor: 'text-[#E1306C]',
    channel: 'instagram',
    action: linkContactInstagram,
    blurb: "Meta only reveals a person's Instagram ID once they've DMed your business account.",
  },
  facebook: {
    label: 'Facebook',
    icon: Facebook,
    iconColor: 'text-[#1877F2]',
    channel: 'messenger',
    action: linkContactFacebook,
    blurb: "Meta only reveals a person's Facebook ID once they've messaged your connected Page.",
  },
};

// Generalized from the original Instagram-only version — same manual-link
// flow, just parameterized by channel so Facebook reuses it instead of a
// near-duplicate component.
export default function LinkInstagramModal({ isOpen, onClose, contactId, onLinked, channel = 'instagram' }) {
  const dispatch = useDispatch();
  const cfg = CHANNEL_CONFIG[channel] || CHANNEL_CONFIG.instagram;
  const Icon = cfg.icon;
  const { conversations } = useSelector(state => state.inbox);
  const [conversationId, setConversationId] = useState('');
  const [linking, setLinking] = useState(false);

  const channelConversations = (conversations || []).filter(c => c.channel === cfg.channel);

  useEffect(() => {
    if (isOpen) dispatch(fetchConversations({ channel: cfg.channel }));
  }, [isOpen, dispatch, cfg.channel]);

  useEffect(() => {
    if (isOpen && channelConversations.length && !conversationId) {
      setConversationId(channelConversations[0]._id);
    }
  }, [isOpen, channelConversations, conversationId]);

  if (!isOpen) return null;

  const handleLink = async () => {
    if (!conversationId) return alertService.error('Error', `Select a ${cfg.label} conversation first`);
    setLinking(true);
    try {
      await dispatch(cfg.action({ id: contactId, conversationId })).unwrap();
      alertService.success(`${cfg.label} Linked`, `This contact can now be sent invoices via ${cfg.label} DM`);
      onLinked?.();
      onClose();
    } catch (err) {
      alertService.error('Error', err || `Failed to link ${cfg.label} conversation`);
    } finally {
      setLinking(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="bg-white rounded-t-lg sm:rounded-lg shadow-[0_2px_8px_rgba(0,0,0,0.16)] w-full max-w-md z-10 max-h-[90vh] overflow-y-auto animate-in zoom-in-95 duration-200">
        <div className="px-4 sm:px-6 py-3 border-b border-[#E5E5E5] flex items-center justify-between bg-white">
          <h3 className="text-[16px] font-bold text-[#2E2E2E] flex items-center gap-2"><Icon size={16} className={cfg.iconColor} /> Link {cfg.label} Conversation</h3>
          <button onClick={onClose} className="text-[#706E6B] hover:text-[#080707]"><X size={18} /></button>
        </div>

        <div className="p-4 sm:p-6 space-y-4">
          <p className="text-[12px] text-[#706E6B]">
            {cfg.blurb} Pick the conversation with this contact to link it — invoices can then be sent to them on {cfg.label}.
          </p>

          <div className="space-y-1.5">
            <label className="text-[11px] font-semibold text-[#706E6B]">{cfg.label} Conversation</label>
            <select
              value={conversationId}
              onChange={e => setConversationId(e.target.value)}
              className="w-full h-9 px-3 bg-white border border-[#D8DDE6] rounded text-[13px] outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]"
            >
              {channelConversations.length === 0 && <option value="">No {cfg.label} conversations found</option>}
              {channelConversations.map(c => (
                <option key={c._id} value={c._id}>{c.contactPhone || c.lastMessagePreview || c._id}</option>
              ))}
            </select>
          </div>

          <div className="pt-4 border-t border-[#E5E5E5] flex justify-end gap-2">
            <button type="button" onClick={onClose} className="bg-white h-9 px-4 border border-[#C9C9C9] rounded text-[13px] font-semibold text-[#0176D3] hover:bg-[#F3F2F2]">Cancel</button>
            <button
              type="button"
              onClick={handleLink}
              disabled={linking || !conversationId}
              className="h-9 px-4 bg-[#0176D3] text-white rounded text-[13px] font-semibold hover:bg-[#014486] disabled:opacity-50"
            >
              {linking ? 'Linking…' : 'Link Conversation'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
